import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  CircularProgress,
  Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import Container from "@mui/material/Container";
import { useParams } from "react-router-dom";
import {
  putBackend,
  postBackend,
  getBackend,
  deleteBackend,
  getFileBackend
} from "../../api/backend";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CustomDatePicker from "../../components/CustomDatePicker";
import Divider from "@mui/material/Divider";
import ItemForm from "../../components/ItemForm";
import { useTheme } from "@mui/material/styles";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import { useAuth } from "../../context/AuthContext";
import { fetchWithCache } from "../../utils/cacheUtils";

export default function WorkOrderDetails() {
  const { uuid } = useParams();
  const [tasks, setTasks] = useState([]);
  const [workOrder, setWorkOrder] = useState({
    title: "",
    description: "",
    date: "",
    status: "",
    internal_number: "",
    images: [],
    files: [],
  });
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [machineData, setMachineData] = useState([]);
  const [tempWorkorder, setTempWorkorder] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [editWorkorder, setEditWorkorder] = useState(false);
  const [users, setUsers] = useState([]);
  const [taskClass, setTasksClass] = useState([]);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editedTask, setEditedTask] = useState({ description: "" });
  const [openForm, setOpenForm] = useState(false);
  const [colors, setColors] = useState({});
  const theme = useTheme();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  const hasPermission = (user) => {
    if (user?.groups) {
      return {
        add: user.groups.some((group) =>
          user.group_permissions[group]?.includes(`Can add task instance`)
        ),
        change: user.groups.some((group) =>
          user.group_permissions[group]?.includes(`Can change task instance`)
        ),
        delete: user.groups.some((group) =>
          user.group_permissions[group]?.includes(`Can delete task instance`)
        ),
      };
    }
  };
  const permissions = React.useMemo(() => hasPermission(user), [user]);


  const handleFileDownload = async (response, filename) => {
    try {
      const contentDisposition = response.headers.get('content-disposition');
      const downloadFilename = contentDisposition
        ? decodeURIComponent(contentDisposition.split('filename=')[1]?.replace(/['"]/g, ''))
        : filename;
  
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  
      return true;
    } catch (error) {
      console.error('Error downloading file:', error);
      return false;
    }
  };


  const handleFileClick = async (fileUuid, filename) => {
    try {
      const response = await getFileBackend(`workorder/${workOrder.uuid}/get-file/`, {
        params: { uuid: fileUuid }
      });
      
      if (response) {
        await handleFileDownload(response, filename);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      // Add error notification here if needed
    }
  };


  const fetchWorkOrderDetails = async () => {
    setLoading(true);
    
    try {
      // Fetch work order specific data (not cached)
      const workOrderResponse = await getBackend(`work_orders/${uuid}`);
      const tasksResponse = await getBackend("task_instances/", {
        params: { work_order: uuid },
      });
      
      // Fetch machine data (not cached as it's specific to this work order)
      const machinesResponse = await getBackend(
        `machines/${workOrderResponse.machine}`
      );
      
      // Use fetchWithCache for reference data
      const usersResponse = await fetchWithCache('users', 
        () => getBackend("users"));
      
      const statusesResponse = await fetchWithCache('statuses', 
        () => getBackend("statuses"));
      
      // Fetch client data (could be cached, but it's specific to this machine)
      const clientsResponse = await getBackend(
        `clients/${machinesResponse.client_name}`
      );
      
      // Fetch task class data (could be cached, but it's specific to this machine type)
      const taskclassResponse = await getBackend(
        `machine_types/${machinesResponse.machine_type}`
      );
  
      // Process the data as before
      setTasksClass(
        Object.fromEntries(
          taskclassResponse.task_class.map((item) => [item.uuid, item.task_class])
        )
      );
      
      setColors(
        Object.fromEntries(
          statusesResponse.map((item) => [item.status, item.color])
        )
      );
      
      setMachineData({
        uuid: machinesResponse.uuid,
        internal_number: machinesResponse.internal_number,
        client: clientsResponse.name,
      });
  
      setUsers(
        Object.fromEntries(
          usersResponse.map((item) => [
            item.uuid,
            `${item.first_name} ${item.last_name}`,
          ])
        )
      );
      
      setStatuses(
        Object.fromEntries(
          statusesResponse.map((item) => [item.uuid, item.status])
        )
      );
  
      if (workOrderResponse && tasksResponse) {
        setWorkOrder({
          uuid: workOrderResponse.uuid,
          description: workOrderResponse.description,
          arrival_date: workOrderResponse.arrival_date,
          pickup_date: workOrderResponse.pickup_date,
          status: workOrderResponse.status,
          user: workOrderResponse.user,
          machine: workOrderResponse.machine,
          internal_number: workOrderResponse.internal_number,
          images: workOrderResponse.images,
          files: workOrderResponse.files,
        });
        setTasks(tasksResponse);
      }
    } catch (error) {
      console.error("Error fetching work order details:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeTask = async (taskId) => {
    setTasks(tasks.filter((t) => t.id !== taskId));
    try {
      await deleteBackend(`task_instances`, taskId);
      fetchWorkOrderDetails();
    } catch (error) {
      console.error("Error deleting task:", error);
      setTasks((prevTasks) => [
        ...prevTasks,
        tasks.find((t) => t.id === taskId),
      ]);
    }
  };

  const handleSaveImages = async (files) => {
    const formData = new FormData();

    if (files instanceof FileList) {
      // Multiple files selected
      for (let i = 0; i < files.length; i++) {
        formData.append("image_files", files[i]);
      }
    } else if (files instanceof File) {
      // Single file (from camera capture or single file selection)
      formData.append("image_files", files);
    } else {
      console.error("Invalid file input");
      return;
    }

    try {
      const response = await postBackend(
        `workorder/${uuid}/upload-images`,
        formData,
        true
      );

      if (response && response.images) {
        setWorkOrder((prev) => ({
          ...prev,
          images: [...prev.images, ...response.images],
        }));
      }
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      handleCloseModal();
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCameraActive(false);
    stopCamera();
  };

  const handleOpenFileImageInput = () => {
    document.getElementById("raised-button-image").click();
  };

  const handleOpenFileInput = () => {
    document.getElementById("raised-button-file").click();
  };
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing camera: ", err);
    }
  };

  const stopCamera = () => {
    const stream = videoRef.current?.srcObject;
    if (stream) {
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
    }
    setCameraActive(false);
  };

  useEffect(() => {
    fetchWorkOrderDetails();
  }, [uuid]);

  const capturePhoto = async () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    handleCloseModal();

    await canvas.toBlob(async (blob) => {
      const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
      await handleSaveImages(file);
      await fetchWorkOrderDetails();
    });
  };

  const handleTakePhoto = () => {
    setOpenModal(true); // Open the modal first
    setCameraActive(true); // Trigger the useEffect to start the camera
  };

  useEffect(() => {
    if (cameraActive && videoRef.current) {
      startCamera();
    }
  }, [cameraActive]);

  const handleDeleteImage = async (uuid) => {
    await deleteBackend(`workorder/${workOrder.uuid}/delete-image`, "", {
      uuid: uuid,
    });

    setOpenConfirm(false);
    fetchWorkOrderDetails();
  };

  const handleDeleteWO = async (id) => {
    await deleteBackend(`work_orders/${workOrder.uuid}`);
    navigate(-1);
  };

  const handleEditWO = async () => {
    setEditWorkorder(true);
    setTempWorkorder(workOrder);
  };

  const handleCancelEditWO = async () => {
    setEditWorkorder(false);
    setTempWorkorder({});
  };

  const handleSaveChanges = async () => {
    const { images, ...data } = tempWorkorder;
    await putBackend(`work_orders`, data);
    setEditWorkorder(false);
    fetchWorkOrderDetails();
  };

  const handleOpenConfirm = (items) => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleAddTask = () => {
    setOpenForm(true);
  };

  const handleAddFile = async (files) => {
    const formData = new FormData();

    if (files.length > 2) {
      alert("Puedes cargar un máximo de 2 archivos.");
      return;
    }
    if (files instanceof FileList) {
      for (let i = 0; i < files.length; i++) {
        formData.append("file_files", files[i]);
      }
    } else if (files instanceof File) {
      formData.append("file_files", files);
    } else {
      console.error("Invalid file input");
      return;
    }

    try {
      const response = await postBackend(
        `workorder/${uuid}/upload-files`,
        formData,
        true
      );
      if (response && response.files) {
        setWorkOrder((prev) => ({
          ...prev,
          files: [...prev.files, ...response.files],
        }));
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };

  const handleDeleteFile = async (uuid) => {
    await deleteBackend(`workorder/${workOrder.uuid}/delete-file`, "", {
      uuid: uuid,
    });

    fetchWorkOrderDetails();
  };

  const handleCloseForm = async () => {
    setOpenForm(false);
    fetchWorkOrderDetails();
  };
  const updateTask = async (updatedTask) => {
    try {
      await putBackend("task_instances", updatedTask);
      fetchWorkOrderDetails();
    } catch (error) {
      console.error("Error al actualizar la tarea:", error);
    }
  };

  const handleEditClick = (task) => {
    setEditingTaskId(task.uuid);
    setEditedTask({ description: task.description });
  };

  const handleSaveClick = (task) => {
    const updatedTask = {
      ...task,
      ...editedTask,
    };
    delete updatedTask.task_name;
    updateTask(updatedTask);
    setEditingTaskId(null);
  };

  const handleCancelClick = () => {
    setEditingTaskId(null);
  };

  const existingTaskClassUUIDs = tasks.map((task) => task.task_class);
  const filteredTaskClasses = Object.fromEntries(
    Object.entries(taskClass).filter(
      ([uuid]) => !existingTaskClassUUIDs.includes(uuid)
    )
  );

  const fieldsConfig = [
    { 
      key: "user",
      label: "Usuario",
      type: "select",
      options: users,
      displayForm: true

    },
    {
      key: "task_class",
      label: "Tarea",
      type: "select",
      options: filteredTaskClasses,
      displayForm: true,
    },
    { 
      key: "description", 
      label: "Descripción",
      type: "textarea",
      displayForm: true
    },
    {
      key: "work_order",
      label: "Work Order",
      type: "text",
      includeInFormSubmit: true,
      value: workOrder.uuid,
    },
  ];

  return loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  ) : (
    <Container maxWidth={false} disableGutters sx={{ paddingTop: 5 }}>
      <Box sx={{ padding: 4 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          {/* Icono de flecha a la izquierda */}
          <Grid item xs={2} textAlign={"left"}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          </Grid>

          {/* Cartel centrado */}
          <Grid item xs={4}>
            <Paper
              elevation={3}
              sx={{ padding: "5px", textAlign: "center", marginBottom: "24px" }}
            >
              <Typography variant="h6" component="div">
                Orden de trabajo Nº {workOrder.internal_number}
              </Typography>
            </Paper>
          </Grid>

          {/* Iconos a la derecha */}
          <Grid item xs={2}>
            <Grid container alignItems={"center"} justifyContent={"flex-end"}>
              {!editWorkorder ? (
                <Grid item>
                  <IconButton onClick={() => handleEditWO()}>
                    <EditIcon color="primary" />
                  </IconButton>
                </Grid>
              ) : (
                <>
                  <Grid item>
                    <IconButton
                      color="success"
                      onClick={() => handleSaveChanges()}
                    >
                      <CheckIcon />
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <IconButton
                      color="error"
                      onClick={() => handleCancelEditWO()}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                </>
              )}

              <Grid item>
                <IconButton
                  variant="contained"
                  color="error"
                  onClick={() => handleOpenConfirm()}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", borderRadius: "8px" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                    fontWeight: "bold",
                  }}
                >
                  Descripción
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                    fontWeight: "bold",
                  }}
                >
                  Máquina - Cliente
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                    fontWeight: "bold",
                  }}
                >
                  Fecha de ingreso
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                    fontWeight: "bold",
                  }}
                >
                  Fecha de salida
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                    fontWeight: "bold",
                  }}
                >
                  Usuario
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                    fontWeight: "bold",
                  }}
                >
                  Estado
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center">
                  {editWorkorder ? (
                    <TextField
                      variant="outlined"
                      value={tempWorkorder.description || ""}
                      onChange={(e) =>
                        setTempWorkorder((prevState) => ({
                          ...prevState,
                          description: e.target.value,
                        }))
                      }
                      sx={{ marginX: 2 }}
                    />
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{ marginX: 2, width: "100%" }}
                    >
                      {workOrder.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body1" sx={{ marginX: 2 }}>
                    <Chip
                      label={`${machineData.internal_number} - ${machineData.client}`}
                    />
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body1" sx={{ marginX: 2 }}>
                    <Chip label={workOrder.arrival_date} />
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body1" sx={{ marginX: 2 }}>
                    {editWorkorder ? (
                      <CustomDatePicker
                        displayText={null}
                        value={tempWorkorder.pickup_date}
                        onChange={(value) =>
                          setTempWorkorder((prevState) => ({
                            ...prevState,
                            pickup_date: value,
                          }))
                        }
                        minDate={workOrder.arrival_date}
                      />
                    ) : (
                      workOrder.pickup_date && (
                        <Chip label={workOrder.pickup_date} />
                      )
                    )}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {editWorkorder ? (
                    <FormControl sx={{ marginX: 2, minWidth: 120 }}>
                      <InputLabel>Usuario</InputLabel>
                      <Select
                        value={tempWorkorder.user}
                        onChange={(e) =>
                          setTempWorkorder((prevState) => ({
                            ...prevState,
                            user: e.target.value,
                          }))
                        }
                        label="Usuario"
                      >
                        {Object.entries(users).map(([key, value]) => (
                          <MenuItem key={key} value={key}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Chip label={users[workOrder.user]} />
                  )}
                </TableCell>
                <TableCell align="center">
                  {editWorkorder ? (
                    <FormControl sx={{ marginX: 2, minWidth: 120 }}>
                      <InputLabel>Estado</InputLabel>
                      <Select
                        value={tempWorkorder.status}
                        onChange={(e) =>
                          setTempWorkorder((prevState) => ({
                            ...prevState,
                            status: e.target.value,
                          }))
                        }
                        label="Status"
                      >
                        {Object.entries(statuses).map(([key, value]) => (
                          <MenuItem key={key} value={key}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Chip
                      sx={{
                        backgroundColor: colors[statuses[workOrder.status]],
                      }}
                      label={statuses[workOrder.status]}
                    />
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <ImageList variant="quilted" cols={4} rowHeight={230}>
          {workOrder.images.map((image) => (
            <ImageListItem
              key={image.uuid}
              cols={1}
              rows={1}
              style={{ position: "relative" }}
            >
              <img
                src={image.image}
                alt={`Work Order Image ${image.id}`}
                style={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "cover",
                }}
                loading="lazy"
              />
              <IconButton
                onClick={() => handleDeleteImage(image.uuid)}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: "white", // Adjust the color if needed
                  backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent background
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </ImageListItem>
          ))}
        </ImageList>

        <Grid container alignItems={"center"} justifyContent={"flex-end"}>
          <Grid item>
            {workOrder.images.length < 4 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenModal}
                startIcon={<AddPhotoAlternateIcon />}
              >
                Añadir Imagen
              </Button>
            )}
          </Grid>
        </Grid>

        {/* Task section */}
        <Divider style={{ marginTop: "2px ", marginBottom: "0px" }}>
          <Chip label="Tareas" size="large" />
        </Divider>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {tasks.map((task) => (
            <Grid item xs={3} key={task.id}>
              <Paper
                sx={{
                  padding: 2,
                  position: "relative",
                  border: "1px solid",
                  borderRadius: 1,
                  boxShadow: 2,
                  minHeight: 220, // Adjusted to give enough space to avoid overlap
                }}
              >
                {/* Botones de editar y eliminar */}
                <Box sx={{ position: "absolute", top: 2, right: 1 }}>
                  {editingTaskId === task.uuid ? (
                    <>
                      <IconButton
                        color="primary"
                        onClick={() => handleSaveClick(task)}
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton color="secondary" onClick={handleCancelClick}>
                        <CloseIcon />
                      </IconButton>
                    </>
                  ) : (
                    permissions?.change && (
                      <IconButton
                        color="primary"
                        onClick={() => handleEditClick(task)}
                      >
                        <EditIcon />
                      </IconButton>
                    )
                  )}

                  {permissions?.delete && (
                    <IconButton
                      color="error"
                      onClick={() => removeTask(task.uuid)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                {editingTaskId === task.uuid ? (
                  <Grid container spacing={2} sx={{ mt: 3 }}>
                    <Grid item xs={12}>
                      <TextField
                        label="Descripción"
                        value={editedTask.description}
                        onChange={(e) =>
                          setEditedTask({
                            ...editedTask,
                            description: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <>
                    <Typography sx={{ marginTop: 2 }} variant="h6">
                      {task.task_name}
                    </Typography>
                    <Typography>Usuario: {users[task.user]}</Typography>
                    <Typography>Fecha inicio: {task.created_date}</Typography>
                    <Typography>Descripción: {task.description}</Typography>
                  </>
                )}
              </Paper>
            </Grid>
          ))}
          <Grid item xs={3}>
            <Paper
              sx={{
                padding: 2,
                border: "2px dashed",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddTask}
              >
                Añadir tarea
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ paddingLeft: 4, paddingRight: 4 }}>
        <Divider style={{ marginTop: "0px " }}>
          <Chip label="Ensayos" size="large" />
        </Divider>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          {/* Archivos cargados */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {workOrder.files.map((file, index) => (
              <Grid item xs={3} key={index}>
                <Paper
                  sx={{
                    padding: 2,
                    display: "flex",
                    alignItems: "center",
                    transition: "background-color 0.2s",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                    // Ensure text doesn't overflow
                    "& span": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }
                  }}
                >
                  <IconButton size="small">
                    <FileIcon />
                  </IconButton>
                  <span style={{ marginLeft: 8, marginRight: 16, flex: 1 }}>
                    {file.file_url?.split("/").pop()}
                  </span>
                  <Tooltip title="Download file" arrow>
                    <IconButton 
                      onClick={() => handleFileClick(file.uuid)}
                      sx={{
                        "&:hover": {
                          color: "primary.main",
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        }
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete file" arrow>
                    <IconButton
                      onClick={() => handleDeleteFile(file.uuid)}
                      sx={{ 
                        marginLeft: 1,
                        "&:hover": {
                          color: "error.main"
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Botón al final de la fila */}
          <Grid item>
            <input
              accept="file/*"
              style={{ display: "none" }}
              id="raised-button-file"
              multiple
              type="file"
              onChange={async (event) => {
                await handleAddFile(event.target.files);
                await fetchWorkOrderDetails();
              }}
            />
            {workOrder.files.length < 3 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenFileInput}
                startIcon={<AddPhotoAlternateIcon />}
              >
                Añadir Ensayo
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
      {openForm && (
        <ItemForm
          item={{ work_order: workOrder.uuid }}
          tableName={"task_instances"}
          onClose={handleCloseForm}
          fieldsConfig={fieldsConfig}
          action="add"
        />
      )}

      {/* Modal for choosing to upload or take photo */}
      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>Elija una opción...</DialogTitle>
        <DialogContent>
          {cameraActive ? (
            <Box>
              <video
                ref={videoRef}
                autoPlay
                style={{ width: "100%", height: "auto", zIndex: "1000" }}
              />
              <Button
                fullWidth
                startIcon={<PhotoCameraIcon />}
                variant="contained"
                color="primary"
                onClick={capturePhoto}
                sx={{ mt: 2 }}
              >
                Capturar
              </Button>
            </Box>
          ) : (
            <Box>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="raised-button-image"
                multiple
                type="file"
                onChange={async (event) => {
                  await handleSaveImages(event.target.files);
                  await fetchWorkOrderDetails();
                }}
              />
              <Button
                fullWidth
                startIcon={<FileUploadIcon />}
                variant="contained"
                color="primary"
                onClick={handleOpenFileImageInput}
              >
                Subir imagen
              </Button>
              <Button
                fullWidth
                startIcon={<PhotoCameraIcon />}
                variant="contained"
                color="secondary"
                onClick={handleTakePhoto}
                sx={{ mt: 2 }}
              >
                Tomar foto
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        openConfirm={openConfirm}
        handleCloseConfirm={handleCloseConfirm}
        handleDelete={handleDeleteWO}
        itemsToDelete={workOrder}
      />

      <canvas
        ref={canvasRef}
        style={{ display: "none" }}
        width={640}
        height={480}
      />
    </Container>
  );
}
